import { Fragment, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

// @mui material components
import Card from "@mui/material/Card";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDAlert from "components/MDAlert";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import DauTrang from "components/DauTrang";
import { giaoDichApi } from "api/giaoDich";
import { lichSuKhoApi } from "api/lichSuKho";
import { chuVuonApi } from "api/chuVuon";
import { nguoiDungApi } from "api/nguoiDung";
import { hopTacXaApi } from "api/hopTacXa";
import { tinhBieu02, tinhBieu03, tongBieu03TuDong, tinhBieu04 } from "lib/tinhChungTuThuMua";
import { xuatExcelBieu02, xuatExcelBieu03, xuatExcelBieu04 } from "lib/xuatExcelChungTu";
import { chuXuongDong, oVienBangExcel } from "lib/kieuBangDuLieu";
import { LAYOUT, MAU, SEMANTIC } from "assets/theme/avocadoBrand";
import useTour from "hooks/useTour";
import buocChungTuThuMua from "tours/chungTuThuMua";

const TEN_CONG_TY = "CÔNG TY TNHH LV NÔNG NGHIỆP";
// Bộ lọc "Nhân viên" ở Biểu 02 chỉ liệt kê đúng vai trò "nhan_vien" (nhân
// viên thu mua) — không lấy cả admin/kế toán/kho dù họ cũng có tài khoản
// trong nguoiDung, vì đây là bộ lọc theo NGƯỜI ĐI THU MUA, không phải danh
// bạ toàn bộ tài khoản hệ thống.
const VAI_TRO_NHAN_VIEN_THU_MUA = ["nhan_vien"];
// "Ngoài hợp tác xã" (Biểu 03) không có maHopTacXa thật (null) — dùng 1 khoá
// giả để chọn được trong dropdown lọc, phân biệt với "" (nghĩa là "Tất cả").
const KHOA_NGOAI_HTX = "__ngoai_htx";

// Bảng dạng "excel view" — kẻ ô đầy đủ 4 cạnh, header xám nhạt, chữ nhỏ gọn —
// thay cho Table kiểu Material (chỉ có viền dưới, thưa dòng) để giống hẳn 1
// trang tính Excel thật, đúng với mục đích chứng từ đối chiếu.
// Màu: borderStrong / border / paper / surface từ avocadoBrand (không hex rải).
const oO = { ...oVienBangExcel, padding: "4px 10px", fontSize: "0.78rem" };
const oOTieuDe = {
  ...oO,
  backgroundColor: SEMANTIC.border,
  fontWeight: 700,
  whiteSpace: "nowrap",
};
const oOTong = { ...oO, backgroundColor: MAU.paper, fontWeight: 700 };
const oONhom = { ...oO, backgroundColor: SEMANTIC.border, fontWeight: 700 };
const oTheNen = { ...oVienBangExcel, backgroundColor: SEMANTIC.surface };
const oTheTong = { ...oVienBangExcel, backgroundColor: MAU.paper };
const oTheNhom = { ...oVienBangExcel, backgroundColor: SEMANTIC.border };

// Ngày theo giờ VN (UTC+7). Không dùng toISOString().slice(0,10) — đó là UTC.
function homNay() {
  const ms = Date.now() + 7 * 60 * 60 * 1000;
  const d = new Date(ms);
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function tien(n) {
  return `${Math.round(n).toLocaleString("vi-VN")} đ`;
}

function kg(n) {
  return `${n.toLocaleString("vi-VN", { maximumFractionDigits: 2 })} kg`;
}

// Lấy toàn bộ giao dịch trong khoảng ngày, kèm dữ liệu nhập kho + PO gốc của
// từng chu kỳ liên quan — dùng cho Biểu 02 (ước lượng phân bổ khối lượng
// nhập kho theo tỷ lệ, xem lib/tinhChungTuThuMua).
async function taiDuLieuMuaVaNhapKho(tuNgay, denNgay) {
  const [danhSachGiaoDich, lichSuKhoTho] = await Promise.all([
    giaoDichApi.trongKhoang(tuNgay, denNgay),
    lichSuKhoApi.trongKhoang(tuNgay, denNgay),
  ]);
  const danhSachNhapKho = lichSuKhoTho.filter((ls) => ls.loai === "nhap");
  const maChuKyList = [...new Set(danhSachNhapKho.map((ls) => ls.chuKyId).filter(Boolean))];
  const itemsTheoChuKy = await giaoDichApi.theoNhieuChuKy(maChuKyList);
  return { danhSachGiaoDich, danhSachNhapKho, itemsTheoChuKy };
}

function BoLocNgay({ tuNgay, denNgay, onDoiTuNgay, onDoiDenNgay }) {
  return (
    <>
      <MDBox
        sx={{
          width: { xs: "calc(50% - 8px)", sm: LAYOUT.filterSelectWidth },
          minWidth: { sm: LAYOUT.filterSelectWidth - 16 },
          flex: { sm: `0 0 ${LAYOUT.filterSelectWidth}px` },
        }}
      >
        <MDInput
          type="date"
          label="Từ ngày"
          value={tuNgay}
          onChange={(e) => onDoiTuNgay(e.target.value)}
          fullWidth
          size="small"
          InputLabelProps={{ shrink: true }}
        />
      </MDBox>
      <MDBox
        sx={{
          width: { xs: "calc(50% - 8px)", sm: LAYOUT.filterSelectWidth },
          minWidth: { sm: LAYOUT.filterSelectWidth - 16 },
          flex: { sm: `0 0 ${LAYOUT.filterSelectWidth}px` },
        }}
      >
        <MDInput
          type="date"
          label="Đến ngày"
          value={denNgay}
          onChange={(e) => onDoiDenNgay(e.target.value)}
          fullWidth
          size="small"
          InputLabelProps={{ shrink: true }}
        />
      </MDBox>
    </>
  );
}

BoLocNgay.propTypes = {
  tuNgay: PropTypes.string.isRequired,
  denNgay: PropTypes.string.isRequired,
  onDoiTuNgay: PropTypes.func.isRequired,
  onDoiDenNgay: PropTypes.func.isRequired,
};

/**
 * Hàng bộ lọc chứng từ: input xếp trái (wrap), nút Xuất Excel tách phải.
 * Tránh flex-end + width hẹp làm select "Nhân viên"/"HTX" bị ép → label đè viền.
 */
function HangBoLoc({ children, nutPhai }) {
  return (
    <MDBox
      mt={2}
      mb={0.5}
      display="flex"
      flexDirection={{ xs: "column", md: "row" }}
      justifyContent="space-between"
      alignItems={{ xs: "stretch", md: "flex-start" }}
      gap={2}
    >
      <MDBox
        display="flex"
        flexWrap="wrap"
        alignItems="flex-end"
        gap={2}
        sx={{
          flex: "1 1 auto",
          minWidth: 0,
        }}
      >
        {children}
      </MDBox>
      {nutPhai ? <MDBox sx={{ flexShrink: 0 }}>{nutPhai}</MDBox> : null}
    </MDBox>
  );
}

HangBoLoc.propTypes = {
  children: PropTypes.node.isRequired,
  nutPhai: PropTypes.node,
};

HangBoLoc.defaultProps = {
  nutPhai: null,
};

/** Ô select bộ lọc: nhãn riêng phía trên — tránh notch outlined của MDInput select bị vỡ. */
function OSelectBoLoc({ id, label, value, onChange, children }) {
  return (
    <MDBox
      id={id}
      sx={{
        flex: "0 0 auto",
        width: { xs: "100%", sm: LAYOUT.filterSelectWidth },
        maxWidth: { sm: LAYOUT.filterSelectWidth },
        alignSelf: "flex-end",
      }}
    >
      <MDTypography
        variant="caption"
        color="text"
        fontWeight="medium"
        display="block"
        mb={0.5}
        sx={{ lineHeight: 1.2 }}
      >
        {label}
      </MDTypography>
      <MDInput
        select
        SelectProps={{ native: true }}
        value={value}
        onChange={onChange}
        fullWidth
        size="small"
      >
        {children}
      </MDInput>
    </MDBox>
  );
}

OSelectBoLoc.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
};

OSelectBoLoc.defaultProps = {
  id: undefined,
};

/** Nút Lọc — không co lại, căn đáy cùng hàng input. */
function NutLocBoLoc({ children, ...rest }) {
  return (
    <MDBox
      sx={{
        flex: "0 0 auto",
        width: { xs: "100%", sm: "auto" },
      }}
    >
      <MDButton {...rest} sx={{ whiteSpace: "nowrap", width: { xs: "100%", sm: "auto" } }}>
        {children}
      </MDButton>
    </MDBox>
  );
}

NutLocBoLoc.propTypes = {
  children: PropTypes.node.isRequired,
};

// Thẻ mobile cho từng dòng chứng từ — thay bảng excel-view dưới md để
// không bắt kéo ngang 8–10 cột trên điện thoại.
function NhanGiaTri({ nhan, giaTri }) {
  return (
    <MDBox minWidth={0}>
      <MDTypography variant="caption" color="text" textTransform="uppercase" display="block">
        {nhan}
      </MDTypography>
      <MDTypography variant="button" fontWeight="regular" display="block" sx={chuXuongDong}>
        {giaTri ?? "—"}
      </MDTypography>
    </MDBox>
  );
}

NhanGiaTri.propTypes = {
  nhan: PropTypes.string.isRequired,
  giaTri: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

NhanGiaTri.defaultProps = {
  giaTri: null,
};

function TheDongMobile({ tieuDe, phuDe, dong, noiBat }) {
  return (
    <MDBox mb={1.5} p={1.5} borderRadius="md" sx={oTheNen}>
      <MDTypography variant="button" fontWeight="bold" display="block" sx={chuXuongDong}>
        {tieuDe}
      </MDTypography>
      {phuDe ? (
        <MDTypography variant="caption" color="text" display="block" mb={0.5} sx={chuXuongDong}>
          {phuDe}
        </MDTypography>
      ) : null}
      {noiBat && noiBat.length > 0 ? (
        <MDBox display="flex" flexWrap="wrap" gap={2} my={1}>
          {noiBat.map((n) => (
            <MDBox key={n.nhan} minWidth={0}>
              <MDTypography
                variant="caption"
                color="text"
                textTransform="uppercase"
                display="block"
              >
                {n.nhan}
              </MDTypography>
              <MDTypography variant="h6" fontWeight="bold" display="block" sx={chuXuongDong}>
                {n.giaTri}
              </MDTypography>
            </MDBox>
          ))}
        </MDBox>
      ) : null}
      <MDBox
        display="grid"
        sx={{
          gridTemplateColumns: "1fr 1fr",
          gap: 1.25,
        }}
      >
        {dong.map((f) => (
          <MDBox key={f.nhan} sx={{ gridColumn: f.full ? "1 / -1" : "auto" }} minWidth={0}>
            <NhanGiaTri nhan={f.nhan} giaTri={f.giaTri} />
          </MDBox>
        ))}
      </MDBox>
    </MDBox>
  );
}

TheDongMobile.propTypes = {
  tieuDe: PropTypes.node.isRequired,
  phuDe: PropTypes.node,
  dong: PropTypes.arrayOf(
    PropTypes.shape({
      nhan: PropTypes.string.isRequired,
      giaTri: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      full: PropTypes.bool,
    })
  ).isRequired,
  noiBat: PropTypes.arrayOf(
    PropTypes.shape({
      nhan: PropTypes.string.isRequired,
      giaTri: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    })
  ),
};

TheDongMobile.defaultProps = {
  phuDe: null,
  noiBat: null,
};

function TheTongMobile({ dong, noiBat }) {
  return (
    <MDBox mb={1.5} p={1.5} borderRadius="md" sx={oTheTong}>
      <MDTypography variant="button" fontWeight="bold" display="block" mb={1}>
        TỔNG CỘNG
      </MDTypography>
      {noiBat && noiBat.length > 0 ? (
        <MDBox display="flex" flexWrap="wrap" gap={2} mb={1}>
          {noiBat.map((n) => (
            <MDBox key={n.nhan} minWidth={0}>
              <MDTypography
                variant="caption"
                color="text"
                textTransform="uppercase"
                display="block"
              >
                {n.nhan}
              </MDTypography>
              <MDTypography variant="h6" fontWeight="bold" display="block" sx={chuXuongDong}>
                {n.giaTri}
              </MDTypography>
            </MDBox>
          ))}
        </MDBox>
      ) : null}
      {dong && dong.length > 0 ? (
        <MDBox
          display="grid"
          sx={{
            gridTemplateColumns: "1fr 1fr",
            gap: 1.25,
          }}
        >
          {dong.map((f) => (
            <MDBox key={f.nhan} sx={{ gridColumn: f.full ? "1 / -1" : "auto" }} minWidth={0}>
              <NhanGiaTri nhan={f.nhan} giaTri={f.giaTri} />
            </MDBox>
          ))}
        </MDBox>
      ) : null}
    </MDBox>
  );
}

TheTongMobile.propTypes = {
  dong: PropTypes.arrayOf(
    PropTypes.shape({
      nhan: PropTypes.string.isRequired,
      giaTri: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      full: PropTypes.bool,
    })
  ),
  noiBat: PropTypes.arrayOf(
    PropTypes.shape({
      nhan: PropTypes.string.isRequired,
      giaTri: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    })
  ),
};

TheTongMobile.defaultProps = {
  dong: null,
  noiBat: null,
};

function ChungTuThuMua() {
  // Mỗi khối lọc theo khoảng/ngày riêng, không dùng chung 1 bộ lọc — và
  // QUAN TRỌNG: không tự tải khi vào trang / đổi ngày — chỉ tải đúng khối nào
  // khi bấm nút "Lọc dữ liệu" của khối đó.
  //
  // Biểu 01: khoảng ngày + dropdown hộ (chỉ hộ có phiếu trong khoảng đã lọc).
  // Biểu 02/03: khoảng ngày + dropdown NV/HTX như trước.

  // Danh sách nhân viên cho bộ lọc Biểu 02 — tải riêng 1 lần từ nguoiDung,
  // KHÔNG suy ra từ dữ liệu giao dịch đã lọc (trước đây làm vậy nên nhân
  // viên nào chưa có giao dịch trong khoảng ngày đang xem thì biến mất khỏi
  // ô lọc dù vẫn còn trong hệ thống).
  const [danhSachNhanVien, setDanhSachNhanVien] = useState([]);
  useEffect(() => {
    nguoiDungApi
      .danhSach()
      .then((ds) =>
        setDanhSachNhanVien(
          ds
            .filter((nv) => VAI_TRO_NHAN_VIEN_THU_MUA.includes(nv.vaiTro))
            .sort((a, b) => (a.hoTen ?? "").localeCompare(b.hoTen ?? "", "vi"))
        )
      )
      .catch(() => {}); // lỗi tải danh sách nhân viên không chặn các khối chính
  }, []);

  // Danh sách hợp tác xã cho Biểu 03 — cùng lý do với danh sách nhân viên ở
  // trên: tải riêng từ collection hopTacXa, KHÔNG suy ra từ giao dịch đã lọc,
  // để hợp tác xã chưa có giao dịch trong kỳ vẫn hiện ra (0 kg, 0 đ) thay vì
  // biến mất khỏi biểu và bộ lọc.
  const [danhSachHopTacXa, setDanhSachHopTacXa] = useState([]);
  useEffect(() => {
    hopTacXaApi
      .danhSachTatCa()
      .then((ds) =>
        setDanhSachHopTacXa(ds.sort((a, b) => a.tenHopTacXa.localeCompare(b.tenHopTacXa, "vi")))
      )
      .catch(() => {});
  }, []);

  // Biểu 01 — null = chưa bấm "Lọc dữ liệu"
  const [tuNgay02, setTuNgay02] = useState(homNay);
  const [denNgay02, setDenNgay02] = useState(homNay);
  const [danhSachGiaoDich02, setDanhSachGiaoDich02] = useState(null);
  const [chuVuonChiTiet02, setChuVuonChiTiet02] = useState({});
  const [maChuVuon02, setMaChuVuon02] = useState("");
  const [dangTai02, setDangTai02] = useState(false);
  const [loi02, setLoi02] = useState("");
  const [dangXuat02, setDangXuat02] = useState(false);

  const [tuNgay03, setTuNgay03] = useState(homNay);
  const [denNgay03, setDenNgay03] = useState(homNay);
  const [bieu03, setBieu03] = useState(null);
  const [dangTai03, setDangTai03] = useState(false);
  const [loi03, setLoi03] = useState("");
  const [dangXuat03, setDangXuat03] = useState(false);
  const [nhanVien03, setNhanVien03] = useState("");

  const [tuNgay04, setTuNgay04] = useState(homNay);
  const [denNgay04, setDenNgay04] = useState(homNay);
  const [bieu04, setBieu04] = useState(null);
  const [dangTai04, setDangTai04] = useState(false);
  const [loi04, setLoi04] = useState("");
  const [dangXuat04, setDangXuat04] = useState(false);
  const [hopTacXa04, setHopTacXa04] = useState("");

  async function locBieu02() {
    if (tuNgay02 > denNgay02) {
      setLoi02("Từ ngày phải trước hoặc bằng Đến ngày.");
      return;
    }
    setDangTai02(true);
    setLoi02("");
    try {
      const danhSachGiaoDich = await giaoDichApi.trongKhoang(tuNgay02, denNgay02);
      const maChuVuonList = [
        ...new Set(danhSachGiaoDich.map((gd) => gd.maChuVuon).filter(Boolean)),
      ];
      const chuVuonChiTiet = await chuVuonApi.taiHoSoChoChungTu(maChuVuonList);
      setDanhSachGiaoDich02(danhSachGiaoDich);
      setChuVuonChiTiet02(chuVuonChiTiet);
      // Giữ lựa chọn hộ nếu vẫn còn trong kỳ mới; không thì về "Tất cả".
      setMaChuVuon02((prev) => (prev && maChuVuonList.includes(prev) ? prev : ""));
    } catch (err) {
      setLoi02(err.message);
    } finally {
      setDangTai02(false);
    }
  }

  async function locBieu03() {
    if (tuNgay03 > denNgay03) {
      setLoi03("Từ ngày phải trước hoặc bằng Đến ngày.");
      return;
    }
    setDangTai03(true);
    setLoi03("");
    // KHÔNG reset nhanVien03 ở đây — cho phép chọn nhân viên trước rồi mới
    // ấn "Lọc dữ liệu", giữ nguyên lựa chọn thay vì xoá về "Tất cả" mỗi lần
    // tải lại.
    try {
      const data = await taiDuLieuMuaVaNhapKho(tuNgay03, denNgay03);
      setBieu03(tinhBieu03(data));
    } catch (err) {
      setLoi03(err.message);
    } finally {
      setDangTai03(false);
    }
  }

  async function locBieu04() {
    if (tuNgay04 > denNgay04) {
      setLoi04("Từ ngày phải trước hoặc bằng Đến ngày.");
      return;
    }
    setDangTai04(true);
    setLoi04("");
    // KHÔNG reset hopTacXa04 ở đây — cùng lý do với nhanVien03 (Biểu 02): cho
    // phép chọn hợp tác xã trước rồi mới ấn "Lọc dữ liệu".
    try {
      const danhSachGiaoDich = await giaoDichApi.trongKhoang(tuNgay04, denNgay04);
      setBieu04(tinhBieu04({ danhSachGiaoDich, dsHopTacXaTatCa: danhSachHopTacXa }));
    } catch (err) {
      setLoi04(err.message);
    } finally {
      setDangTai04(false);
    }
  }

  // Options hộ — chỉ từ giao dịch đã tải trong khoảng (không list toàn bộ chuVuon).
  const dsHo02 = useMemo(() => {
    if (!danhSachGiaoDich02) return [];
    return [
      ...new Map(
        danhSachGiaoDich02
          .filter((gd) => gd.maChuVuon)
          .map((gd) => [gd.maChuVuon, { maChuVuon: gd.maChuVuon, tenChuVuon: gd.tenChuVuon }])
      ).values(),
    ].sort((a, b) => (a.tenChuVuon ?? "").localeCompare(b.tenChuVuon ?? "", "vi"));
  }, [danhSachGiaoDich02]);

  // Lọc client-side theo hộ rồi tính bảng — không gọi lại Firestore.
  const bieu02Loc = useMemo(() => {
    if (!danhSachGiaoDich02) return null;
    const gdLoc = maChuVuon02
      ? danhSachGiaoDich02.filter((gd) => gd.maChuVuon === maChuVuon02)
      : danhSachGiaoDich02;
    return tinhBieu02({ danhSachGiaoDich: gdLoc, chuVuonChiTiet: chuVuonChiTiet02 });
  }, [danhSachGiaoDich02, chuVuonChiTiet02, maChuVuon02]);

  const dsNhanVien03 = useMemo(
    () =>
      danhSachNhanVien.map((nv) => ({
        maNhanVien: nv.id,
        tenNhanVien: nv.hoTen ?? nv.email,
      })),
    [danhSachNhanVien]
  );

  // Lọc client-side trên dữ liệu đã tải — không cần gọi lại Firestore, tính
  // lại dòng TỔNG CỘNG cho đúng riêng nhân viên đã chọn.
  const bieu03Loc = useMemo(() => {
    if (!bieu03) return null;
    if (!nhanVien03) return bieu03;
    const dong = bieu03.dong.filter((d) => d.maNhanVien === nhanVien03);
    return { dong, tong: tongBieu03TuDong(dong) };
  }, [bieu03, nhanVien03]);

  // Lấy từ danhSachHopTacXa (tải riêng lúc vào trang) thay vì suy ra từ
  // bieu04.nhom — cùng lý do với dsNhanVien03: chọn được hợp tác xã TRƯỚC khi
  // ấn "Lọc dữ liệu" lần đầu, không phải đợi có dữ liệu mới hiện lựa chọn.
  const dsHopTacXa04 = useMemo(
    () => [
      ...danhSachHopTacXa.map((htx) => ({ khoa: htx.id, tenHopTacXa: htx.tenHopTacXa })),
      { khoa: KHOA_NGOAI_HTX, tenHopTacXa: "Ngoài hợp tác xã (mua trực tiếp)" },
    ],
    [danhSachHopTacXa]
  );

  // Lọc client-side trên dữ liệu đã tải — chỉ giữ lại đúng 1 nhóm hợp tác xã
  // đã chọn, không cần gọi lại Firestore.
  const bieu04Loc = useMemo(() => {
    if (!bieu04) return null;
    if (!hopTacXa04) return bieu04;
    const nhom = bieu04.nhom.filter((n) => (n.maHopTacXa ?? KHOA_NGOAI_HTX) === hopTacXa04);
    const tongChung = nhom.reduce(
      (t, n) => ({ slMua: t.slMua + n.tong.slMua, tienMua: t.tienMua + n.tong.tienMua }),
      { slMua: 0, tienMua: 0 }
    );
    return { nhom, tongChung };
  }, [bieu04, hopTacXa04]);

  async function xuatBieu02() {
    setDangXuat02(true);
    try {
      await xuatExcelBieu02(bieu02Loc, { tuNgay: tuNgay02, denNgay: denNgay02 });
    } catch (err) {
      setLoi02(err.message);
    } finally {
      setDangXuat02(false);
    }
  }

  async function xuatBieu03() {
    setDangXuat03(true);
    try {
      await xuatExcelBieu03(bieu03Loc, { tuNgay: tuNgay03, denNgay: denNgay03 });
    } catch (err) {
      setLoi03(err.message);
    } finally {
      setDangXuat03(false);
    }
  }

  async function xuatBieu04() {
    setDangXuat04(true);
    try {
      await xuatExcelBieu04(bieu04Loc, { tuNgay: tuNgay04, denNgay: denNgay04 });
    } catch (err) {
      setLoi04(err.message);
    } finally {
      setDangXuat04(false);
    }
  }

  useTour("chung-tu-thu-mua", buocChungTuThuMua());

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <DauTrang
          nhan="Quản lý"
          tieuDe="Chứng từ thu mua"
          moTa='Biểu 01 chi tiết theo hộ (lọc thêm hộ nông dân), Biểu 02 theo nhân viên thu mua, Biểu 03 theo hợp tác xã (điểm thu mua) — mỗi biểu tự lọc theo khoảng ngày riêng, bấm "Lọc dữ liệu" mới tải, xuất được ra Excel.'
        />

        {/* Biểu 01 */}
        <Card id="tour-chung-tu-thu-mua-bieu01" sx={{ mb: 3 }}>
          <MDBox p={3} pb={1}>
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="flex-start"
              flexWrap="wrap"
              gap={1}
            >
              <MDTypography variant="caption" color="text" fontWeight="bold">
                {TEN_CONG_TY}
              </MDTypography>
              <MDTypography variant="caption" color="text">
                Biểu 01
              </MDTypography>
            </MDBox>
            <MDTypography variant="h6" mt={1}>
              Bảng kê chi tiết thu mua từ các hộ nông dân
            </MDTypography>
            <HangBoLoc
              nutPhai={
                <MDButton
                  id="tour-chung-tu-thu-mua-xuat-bieu01"
                  variant="outlined"
                  color="dark"
                  size="small"
                  disabled={!bieu02Loc || dangXuat02}
                  onClick={xuatBieu02}
                >
                  {dangXuat02 ? "Đang xuất..." : "Xuất Excel"}
                </MDButton>
              }
            >
              <BoLocNgay
                tuNgay={tuNgay02}
                denNgay={denNgay02}
                onDoiTuNgay={setTuNgay02}
                onDoiDenNgay={setDenNgay02}
              />
              <OSelectBoLoc
                id="tour-chung-tu-thu-mua-ho-bieu01"
                label="Hộ nông dân"
                value={maChuVuon02}
                onChange={(e) => setMaChuVuon02(e.target.value)}
              >
                <option value="">Tất cả hộ</option>
                {dsHo02.map((ho) => (
                  <option key={ho.maChuVuon} value={ho.maChuVuon}>
                    {ho.tenChuVuon ?? ho.maChuVuon}
                  </option>
                ))}
              </OSelectBoLoc>
              <NutLocBoLoc
                id="tour-chung-tu-thu-mua-loc-bieu01"
                variant="gradient"
                color="info"
                size="small"
                onClick={locBieu02}
                disabled={dangTai02}
              >
                {dangTai02 ? "Đang lọc..." : "Lọc dữ liệu"}
              </NutLocBoLoc>
            </HangBoLoc>
          </MDBox>

          {loi02 && (
            <MDBox px={3} pb={1}>
              <MDAlert color="error">{loi02}</MDAlert>
            </MDBox>
          )}

          {!bieu02Loc ? (
            <MDBox px={3} pb={3}>
              <MDTypography variant="button" color="text">
                {dangTai02
                  ? "Đang tải..."
                  : "Chọn khoảng ngày rồi bấm “Lọc dữ liệu” để xem. Có thể chọn hộ trước hoặc sau khi lọc."}
              </MDTypography>
            </MDBox>
          ) : bieu02Loc.dong.length === 0 ? (
            <MDBox px={3} pb={3}>
              <MDTypography variant="button" color="text">
                Không có dữ liệu trong khoảng đã chọn
                {maChuVuon02 ? " cho hộ này" : ""}.
              </MDTypography>
            </MDBox>
          ) : (
            <>
              <MDBox sx={{ display: { xs: "none", md: "block" }, overflowX: "auto", px: 3 }}>
                <Table sx={{ borderCollapse: "collapse" }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={oOTieuDe}>STT</TableCell>
                      <TableCell sx={oOTieuDe}>Ngày</TableCell>
                      <TableCell sx={oOTieuDe}>Tên hộ nông dân</TableCell>
                      <TableCell sx={oOTieuDe}>CCCD</TableCell>
                      <TableCell sx={{ ...oOTieuDe, whiteSpace: "normal" }}>Địa chỉ</TableCell>
                      <TableCell sx={oOTieuDe}>Loại bơ</TableCell>
                      <TableCell align="right" sx={oOTieuDe}>
                        Số lượng (kg)
                      </TableCell>
                      <TableCell align="right" sx={oOTieuDe}>
                        Đơn giá (đ/kg)
                      </TableCell>
                      <TableCell align="right" sx={oOTieuDe}>
                        Thành tiền (đ)
                      </TableCell>
                      <TableCell sx={{ ...oOTieuDe, whiteSpace: "normal" }}>
                        Ghi chú chứng từ đầu vào
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bieu02Loc.dong.map((d, i) => (
                      <TableRow key={`${d.maChuVuon}_${d.maLoaiBo}_${d.thoiGian}_${i}`}>
                        <TableCell sx={oO}>{d.stt}</TableCell>
                        <TableCell sx={{ ...oO, whiteSpace: "nowrap" }}>
                          {new Date(d.thoiGian).toLocaleDateString("vi-VN")}
                        </TableCell>
                        <TableCell sx={{ ...oO, whiteSpace: "nowrap" }}>
                          {d.tenChuVuon ?? "—"}
                        </TableCell>
                        <TableCell sx={{ ...oO, whiteSpace: "nowrap" }}>{d.cccd}</TableCell>
                        <TableCell sx={oO}>{d.diaChi}</TableCell>
                        <TableCell sx={{ ...oO, whiteSpace: "nowrap" }}>
                          {d.tenLoaiBo ?? "—"}
                        </TableCell>
                        <TableCell align="right" sx={{ ...oO, whiteSpace: "nowrap" }}>
                          {kg(d.soLuong)}
                        </TableCell>
                        <TableCell align="right" sx={{ ...oO, whiteSpace: "nowrap" }}>
                          {tien(d.donGia)}
                        </TableCell>
                        <TableCell align="right" sx={{ ...oO, whiteSpace: "nowrap" }}>
                          {tien(d.thanhTien)}
                        </TableCell>
                        <TableCell sx={oO}>{d.ghiChu}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell colSpan={6} sx={oOTong}>
                        TỔNG CỘNG
                      </TableCell>
                      <TableCell align="right" sx={{ ...oOTong, whiteSpace: "nowrap" }}>
                        {kg(bieu02Loc.tong.soLuong)}
                      </TableCell>
                      <TableCell align="right" sx={oOTong}>
                        —
                      </TableCell>
                      <TableCell align="right" sx={{ ...oOTong, whiteSpace: "nowrap" }}>
                        {tien(bieu02Loc.tong.thanhTien)}
                      </TableCell>
                      <TableCell sx={oOTong} />
                    </TableRow>
                  </TableBody>
                </Table>
              </MDBox>
              <MDBox sx={{ display: { xs: "block", md: "none" }, px: 3 }}>
                {bieu02Loc.dong.map((d, i) => (
                  <TheDongMobile
                    key={`${d.maChuVuon}_${d.maLoaiBo}_${d.thoiGian}_${i}`}
                    tieuDe={d.tenChuVuon ?? "—"}
                    phuDe={`#${d.stt} · ${new Date(d.thoiGian).toLocaleDateString("vi-VN")} · ${
                      d.tenLoaiBo ?? "—"
                    }`}
                    noiBat={[
                      { nhan: "Số lượng", giaTri: kg(d.soLuong) },
                      { nhan: "Thành tiền", giaTri: tien(d.thanhTien) },
                    ]}
                    dong={[
                      { nhan: "CCCD", giaTri: d.cccd || "—" },
                      { nhan: "Đơn giá", giaTri: tien(d.donGia) },
                      { nhan: "Địa chỉ", giaTri: d.diaChi || "—", full: true },
                      { nhan: "Ghi chú", giaTri: d.ghiChu || "—", full: true },
                    ]}
                  />
                ))}
                <TheTongMobile
                  noiBat={[
                    { nhan: "Số lượng", giaTri: kg(bieu02Loc.tong.soLuong) },
                    { nhan: "Thành tiền", giaTri: tien(bieu02Loc.tong.thanhTien) },
                  ]}
                />
              </MDBox>
            </>
          )}

          <MDBox px={3} py={2}>
            <MDTypography variant="caption" color="text" display="block">
              Lập kèm Bảng kê 01/TNDN — Thuế yêu cầu cơ sở kinh doanh phải minh bạch nguồn gốc nông
              sản đầu vào (bơ thu mua từ nông dân thường được miễn thuế GTGT đầu vào). Dropdown hộ
              chỉ liệt kê chủ vườn có phiếu trong ngày đã lọc.
            </MDTypography>
          </MDBox>
        </Card>

        {/* Biểu 02 */}
        <Card id="tour-chung-tu-thu-mua-bieu02">
          <MDBox p={3} pb={1}>
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="flex-start"
              flexWrap="wrap"
              gap={1}
            >
              <MDTypography variant="caption" color="text" fontWeight="bold">
                {TEN_CONG_TY}
              </MDTypography>
              <MDTypography variant="caption" color="text">
                Biểu 02
              </MDTypography>
            </MDBox>
            <MDTypography variant="h6" mt={1}>
              Bảng tổng hợp theo nhân viên thu mua
            </MDTypography>
            <MDTypography variant="caption" color="text">
              Từng PO thu mua riêng lẻ, không gộp — sắp theo nhân viên.
            </MDTypography>
            <HangBoLoc
              nutPhai={
                <MDButton
                  variant="outlined"
                  color="dark"
                  size="small"
                  disabled={!bieu03Loc || dangXuat03}
                  onClick={xuatBieu03}
                >
                  {dangXuat03 ? "Đang xuất..." : "Xuất Excel"}
                </MDButton>
              }
            >
              <BoLocNgay
                tuNgay={tuNgay03}
                denNgay={denNgay03}
                onDoiTuNgay={setTuNgay03}
                onDoiDenNgay={setDenNgay03}
              />
              <OSelectBoLoc
                id="tour-chung-tu-thu-mua-nv-bieu02"
                label="Nhân viên"
                value={nhanVien03}
                onChange={(e) => setNhanVien03(e.target.value)}
              >
                <option value="">Tất cả</option>
                {dsNhanVien03.map((nv) => (
                  <option key={nv.maNhanVien} value={nv.maNhanVien}>
                    {nv.tenNhanVien}
                  </option>
                ))}
              </OSelectBoLoc>
              <NutLocBoLoc
                variant="gradient"
                color="info"
                size="small"
                onClick={locBieu03}
                disabled={dangTai03}
              >
                {dangTai03 ? "Đang lọc..." : "Lọc dữ liệu"}
              </NutLocBoLoc>
            </HangBoLoc>
          </MDBox>

          {loi03 && (
            <MDBox px={3} pb={1}>
              <MDAlert color="error">{loi03}</MDAlert>
            </MDBox>
          )}

          {!bieu03Loc ? (
            <MDBox px={3} pb={3}>
              <MDTypography variant="button" color="text">
                {dangTai03 ? "Đang tải..." : "Chọn khoảng ngày rồi bấm “Lọc dữ liệu” để xem."}
              </MDTypography>
            </MDBox>
          ) : bieu03Loc.dong.length === 0 ? (
            <MDBox px={3} pb={3}>
              <MDTypography variant="button" color="text">
                Không có dữ liệu trong khoảng ngày đã chọn.
              </MDTypography>
            </MDBox>
          ) : (
            <>
              <MDBox sx={{ display: { xs: "none", md: "block" }, overflowX: "auto", px: 3 }}>
                <Table sx={{ borderCollapse: "collapse" }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={oOTieuDe}>Người thu mua</TableCell>
                      <TableCell sx={oOTieuDe}>Ngày giờ</TableCell>
                      <TableCell sx={oOTieuDe}>Chủ vườn</TableCell>
                      <TableCell sx={oOTieuDe}>Mã Sọt</TableCell>
                      <TableCell align="right" sx={oOTieuDe}>
                        SL mua (kg)
                      </TableCell>
                      <TableCell align="right" sx={oOTieuDe}>
                        Đơn giá (đ/kg)
                      </TableCell>
                      <TableCell align="right" sx={oOTieuDe}>
                        Thành tiền (đ)
                      </TableCell>
                      <TableCell align="right" sx={oOTieuDe}>
                        SL nhập kho (kg)
                      </TableCell>
                      <TableCell align="right" sx={oOTieuDe}>
                        SL hao hụt (kg)
                      </TableCell>
                      <TableCell align="right" sx={oOTieuDe}>
                        Tỷ lệ hao hụt
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bieu03Loc.dong.map((d) => (
                      <TableRow key={d.id}>
                        <TableCell sx={{ ...oO, whiteSpace: "nowrap", fontWeight: 600 }}>
                          {d.tenNhanVien}
                        </TableCell>
                        <TableCell sx={{ ...oO, whiteSpace: "nowrap" }}>
                          {new Date(d.thoiGian).toLocaleString("vi-VN")}
                        </TableCell>
                        <TableCell sx={{ ...oO, whiteSpace: "nowrap" }}>{d.tenChuVuon}</TableCell>
                        <TableCell sx={{ ...oO, whiteSpace: "nowrap" }}>{d.maSotHienThi}</TableCell>
                        <TableCell align="right" sx={{ ...oO, whiteSpace: "nowrap" }}>
                          {kg(d.slMua)}
                        </TableCell>
                        <TableCell align="right" sx={{ ...oO, whiteSpace: "nowrap" }}>
                          {tien(d.donGiaMua)}
                        </TableCell>
                        <TableCell align="right" sx={{ ...oO, whiteSpace: "nowrap" }}>
                          {tien(d.tienMua)}
                        </TableCell>
                        <TableCell align="right" sx={{ ...oO, whiteSpace: "nowrap" }}>
                          {d.slNhapKho != null ? kg(d.slNhapKho) : "—"}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            ...oO,
                            whiteSpace: "nowrap",
                            color: d.chuaNhapKho
                              ? "text.secondary"
                              : d.haoHut < 0
                              ? "error.main"
                              : d.haoHut > 0
                              ? "info.main"
                              : "text.primary",
                          }}
                        >
                          {d.chuaNhapKho ? "0 (Chưa nhập kho)" : kg(d.haoHut)}
                        </TableCell>
                        <TableCell align="right" sx={{ ...oO, whiteSpace: "nowrap" }}>
                          {d.tyLeHaoHut != null ? `${(d.tyLeHaoHut * 100).toFixed(1)}%` : "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell colSpan={4} sx={oOTong}>
                        TỔNG CỘNG
                      </TableCell>
                      <TableCell align="right" sx={{ ...oOTong, whiteSpace: "nowrap" }}>
                        {kg(bieu03Loc.tong.slMua)}
                      </TableCell>
                      <TableCell align="right" sx={oOTong}>
                        —
                      </TableCell>
                      <TableCell align="right" sx={{ ...oOTong, whiteSpace: "nowrap" }}>
                        {tien(bieu03Loc.tong.tienMua)}
                      </TableCell>
                      <TableCell align="right" sx={{ ...oOTong, whiteSpace: "nowrap" }}>
                        {kg(bieu03Loc.tong.slNhapKho)}
                      </TableCell>
                      <TableCell align="right" sx={{ ...oOTong, whiteSpace: "nowrap" }}>
                        {kg(bieu03Loc.tong.haoHut)}
                      </TableCell>
                      <TableCell align="right" sx={{ ...oOTong, whiteSpace: "nowrap" }}>
                        {(bieu03Loc.tong.tyLeHaoHut * 100).toFixed(1)}%
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </MDBox>
              <MDBox sx={{ display: { xs: "block", md: "none" }, px: 3 }}>
                {bieu03Loc.dong.map((d) => (
                  <TheDongMobile
                    key={d.id}
                    tieuDe={d.tenNhanVien ?? "—"}
                    phuDe={`${d.tenChuVuon ?? "—"} · ${new Date(d.thoiGian).toLocaleString(
                      "vi-VN"
                    )}`}
                    noiBat={[
                      { nhan: "SL mua", giaTri: kg(d.slMua) },
                      { nhan: "Thành tiền", giaTri: tien(d.tienMua) },
                    ]}
                    dong={[
                      { nhan: "Mã Sọt", giaTri: d.maSotHienThi || "—" },
                      { nhan: "Đơn giá", giaTri: tien(d.donGiaMua) },
                      {
                        nhan: "SL nhập kho",
                        giaTri: d.slNhapKho != null ? kg(d.slNhapKho) : "—",
                      },
                      {
                        nhan: "SL hao hụt",
                        giaTri: d.chuaNhapKho ? "0 (Chưa nhập kho)" : kg(d.haoHut),
                      },
                      {
                        nhan: "Tỷ lệ hao hụt",
                        giaTri: d.tyLeHaoHut != null ? `${(d.tyLeHaoHut * 100).toFixed(1)}%` : "—",
                      },
                    ]}
                  />
                ))}
                <TheTongMobile
                  noiBat={[
                    { nhan: "SL mua", giaTri: kg(bieu03Loc.tong.slMua) },
                    { nhan: "Thành tiền", giaTri: tien(bieu03Loc.tong.tienMua) },
                  ]}
                  dong={[
                    { nhan: "SL nhập kho", giaTri: kg(bieu03Loc.tong.slNhapKho) },
                    { nhan: "SL hao hụt", giaTri: kg(bieu03Loc.tong.haoHut) },
                    {
                      nhan: "Tỷ lệ hao hụt",
                      giaTri: `${(bieu03Loc.tong.tyLeHaoHut * 100).toFixed(1)}%`,
                    },
                  ]}
                />
              </MDBox>
            </>
          )}

          <MDBox px={3} py={2}>
            <MDTypography variant="caption" color="text" display="block">
              * Cột &quot;SL nhập kho&quot;/&quot;hao hụt&quot; là số ước lượng, phân bổ theo tỷ lệ
              đóng góp của từng PO vào khối lượng cân lại lúc nhập kho của cả Sọt. Hiện
              &quot;—&quot; nếu Sọt của PO đó chưa nhập kho trong khoảng ngày đã chọn.
            </MDTypography>
          </MDBox>
        </Card>

        {/* Biểu 03 */}
        <Card id="tour-chung-tu-thu-mua-bieu03" sx={{ mt: 3 }}>
          <MDBox p={3} pb={1}>
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="flex-start"
              flexWrap="wrap"
              gap={1}
            >
              <MDTypography variant="caption" color="text" fontWeight="bold">
                {TEN_CONG_TY}
              </MDTypography>
              <MDTypography variant="caption" color="text">
                Biểu 03
              </MDTypography>
            </MDBox>
            <MDTypography variant="h6" mt={1}>
              Bảng tổng hợp thu mua theo hợp tác xã (điểm thu mua)
            </MDTypography>
            <MDTypography variant="caption" color="text">
              Từng hợp tác xã — chi tiết số lượng theo từng loại bơ đã thu mua qua điểm đó.
            </MDTypography>
            <HangBoLoc
              nutPhai={
                <MDButton
                  variant="outlined"
                  color="dark"
                  size="small"
                  disabled={!bieu04Loc || dangXuat04}
                  onClick={xuatBieu04}
                >
                  {dangXuat04 ? "Đang xuất..." : "Xuất Excel"}
                </MDButton>
              }
            >
              <BoLocNgay
                tuNgay={tuNgay04}
                denNgay={denNgay04}
                onDoiTuNgay={setTuNgay04}
                onDoiDenNgay={setDenNgay04}
              />
              <OSelectBoLoc
                id="tour-chung-tu-thu-mua-htx-bieu03"
                label="Hợp tác xã"
                value={hopTacXa04}
                onChange={(e) => setHopTacXa04(e.target.value)}
              >
                <option value="">Tất cả</option>
                {dsHopTacXa04.map((htx) => (
                  <option key={htx.khoa} value={htx.khoa}>
                    {htx.tenHopTacXa}
                  </option>
                ))}
              </OSelectBoLoc>
              <NutLocBoLoc
                variant="gradient"
                color="info"
                size="small"
                onClick={locBieu04}
                disabled={dangTai04}
              >
                {dangTai04 ? "Đang lọc..." : "Lọc dữ liệu"}
              </NutLocBoLoc>
            </HangBoLoc>
          </MDBox>

          {loi04 && (
            <MDBox px={3} pb={1}>
              <MDAlert color="error">{loi04}</MDAlert>
            </MDBox>
          )}

          {!bieu04Loc ? (
            <MDBox px={3} pb={3}>
              <MDTypography variant="button" color="text">
                {dangTai04 ? "Đang tải..." : "Chọn khoảng ngày rồi bấm “Lọc dữ liệu” để xem."}
              </MDTypography>
            </MDBox>
          ) : bieu04Loc.nhom.length === 0 ? (
            <MDBox px={3} pb={3}>
              <MDTypography variant="button" color="text">
                Không có dữ liệu trong khoảng ngày đã chọn.
              </MDTypography>
            </MDBox>
          ) : (
            <>
              <MDBox sx={{ display: { xs: "none", md: "block" }, overflowX: "auto", px: 3 }}>
                <Table sx={{ borderCollapse: "collapse" }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={oOTieuDe}>Hợp tác xã (điểm thu mua)</TableCell>
                      <TableCell sx={oOTieuDe}>Người thu mua</TableCell>
                      <TableCell sx={oOTieuDe}>Loại bơ</TableCell>
                      <TableCell align="right" sx={oOTieuDe}>
                        SL mua (kg)
                      </TableCell>
                      <TableCell align="right" sx={oOTieuDe}>
                        Đơn giá mua (đ/kg)
                      </TableCell>
                      <TableCell align="right" sx={oOTieuDe}>
                        Thành tiền mua (đ)
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bieu04Loc.nhom.map((n) => (
                      <Fragment key={n.maHopTacXa ?? KHOA_NGOAI_HTX}>
                        <TableRow>
                          <TableCell colSpan={6} sx={oONhom}>
                            {n.tenHopTacXa} — {kg(n.tong.slMua)} — {tien(n.tong.tienMua)}
                          </TableCell>
                        </TableRow>
                        {n.dong.map((d) => (
                          <TableRow
                            key={`${n.maHopTacXa ?? "ngoai"}_${d.maNhanVien ?? "khac"}_${
                              d.maLoaiBo
                            }`}
                          >
                            <TableCell sx={oO} />
                            <TableCell sx={{ ...oO, whiteSpace: "nowrap" }}>
                              {d.tenNhanVien}
                            </TableCell>
                            <TableCell sx={{ ...oO, whiteSpace: "nowrap" }}>
                              {d.tenLoaiBo}
                            </TableCell>
                            <TableCell align="right" sx={{ ...oO, whiteSpace: "nowrap" }}>
                              {kg(d.slMua)}
                            </TableCell>
                            <TableCell align="right" sx={{ ...oO, whiteSpace: "nowrap" }}>
                              {tien(d.donGiaMua)}
                            </TableCell>
                            <TableCell align="right" sx={{ ...oO, whiteSpace: "nowrap" }}>
                              {tien(d.tienMua)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </Fragment>
                    ))}
                    <TableRow>
                      <TableCell colSpan={3} sx={oOTong}>
                        TỔNG CỘNG
                      </TableCell>
                      <TableCell align="right" sx={{ ...oOTong, whiteSpace: "nowrap" }}>
                        {kg(bieu04Loc.tongChung.slMua)}
                      </TableCell>
                      <TableCell align="right" sx={oOTong}>
                        —
                      </TableCell>
                      <TableCell align="right" sx={{ ...oOTong, whiteSpace: "nowrap" }}>
                        {tien(bieu04Loc.tongChung.tienMua)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </MDBox>
              <MDBox sx={{ display: { xs: "block", md: "none" }, px: 3 }}>
                {bieu04Loc.nhom.map((n) => (
                  <Fragment key={n.maHopTacXa ?? KHOA_NGOAI_HTX}>
                    <MDBox mb={1.5} p={1.5} borderRadius="md" sx={oTheNhom}>
                      <MDTypography
                        variant="button"
                        fontWeight="bold"
                        display="block"
                        sx={chuXuongDong}
                      >
                        {n.tenHopTacXa}
                      </MDTypography>
                      <MDTypography variant="caption" color="text" display="block">
                        {kg(n.tong.slMua)} · {tien(n.tong.tienMua)}
                      </MDTypography>
                    </MDBox>
                    {n.dong.map((d) => (
                      <TheDongMobile
                        key={`${n.maHopTacXa ?? "ngoai"}_${d.maNhanVien ?? "khac"}_${d.maLoaiBo}`}
                        tieuDe={d.tenLoaiBo ?? "—"}
                        phuDe={d.tenNhanVien ?? "—"}
                        noiBat={[
                          { nhan: "SL mua", giaTri: kg(d.slMua) },
                          { nhan: "Thành tiền mua", giaTri: tien(d.tienMua) },
                        ]}
                        dong={[{ nhan: "Đơn giá mua", giaTri: tien(d.donGiaMua) }]}
                      />
                    ))}
                  </Fragment>
                ))}
                <TheTongMobile
                  noiBat={[
                    { nhan: "SL mua", giaTri: kg(bieu04Loc.tongChung.slMua) },
                    { nhan: "Thành tiền mua", giaTri: tien(bieu04Loc.tongChung.tienMua) },
                  ]}
                />
              </MDBox>
            </>
          )}

          <MDBox px={3} py={2}>
            <MDTypography variant="caption" color="text" display="block">
              Nhóm &quot;Ngoài hợp tác xã (mua trực tiếp)&quot; gồm các PO không gắn hợp tác xã nào
              lúc thu mua — giữ lại để tổng biểu này khớp tổng thu mua trong kỳ, không phải điểm thu
              mua thật.
            </MDTypography>
          </MDBox>
        </Card>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default ChungTuThuMua;
